// store/currency.js
import API from "@/api/api-admin";

const CURRENCY_MAP = { euro: "EUR", pound: "GBP" };
const DEFAULT_CURRENCY = CURRENCY_MAP.euro;
const VALID_CURRENCIES = Object.values(CURRENCY_MAP);

const normalizeCurrency = (currency) => {
  if (!currency) return DEFAULT_CURRENCY;
  return CURRENCY_MAP[currency.toLowerCase()] || currency;
};

const state = {
  currency: normalizeCurrency(localStorage.getItem("userCurrency")),
  economicZoneClients: [],
  loadingEconomicZoneClients: false,
  economicZoneClientsError: null,
};

const getters = {
  currentCurrency: (state) => state.currency,
  getEconomicZoneClients: (state) => state.economicZoneClients,
  validCurrencies: () => VALID_CURRENCIES,
};

const mutations = {
  SET_CURRENCY(state, currency) {
    state.currency = currency;
  },
  SET_ECONOMIC_ZONE_CLIENTS(state, clients) {
    state.economicZoneClients = clients;
  },
  SET_ECONOMIC_ZONE_CLIENTS_LOADING(state, status) {
    state.loadingEconomicZoneClients = status;
  },
  SET_ECONOMIC_ZONE_CLIENTS_ERROR(state, error) {
    state.economicZoneClientsError = error;
  },
};

const actions = {
  async updateCurrency({ commit, dispatch }, newCurrency) {
    if (!newCurrency) {
      console.error("Tentative de mise à jour avec une devise invalide");
      return;
    }
    const normalizedCurrency = normalizeCurrency(newCurrency);
    commit("SET_CURRENCY", normalizedCurrency);
    // Persister dans localStorage pour conserver la currency après refresh
    localStorage.setItem("userCurrency", normalizedCurrency);
    await dispatch("fetchEconomicZoneClients", normalizedCurrency);
  },

  async initCurrency({ dispatch, state, rootState }) {
    // Si une currency valide est déjà sauvegardée dans localStorage, la conserver
    const savedCurrency = localStorage.getItem("userCurrency");
    if (savedCurrency && VALID_CURRENCIES.includes(savedCurrency)) {
      // S'assurer que le store est synchronisé avec localStorage
      if (state.currency !== savedCurrency) {
        await dispatch("updateCurrency", savedCurrency);
      } else {
        // Juste charger les clients de la zone économique
        await dispatch("fetchEconomicZoneClients", savedCurrency);
      }
      return;
    }

    // Sinon, utiliser la currency de l'utilisateur
    const activeUser = rootState?.temporaryUser || rootState.user;
    const userCurrency = activeUser?.currency;

    if (userCurrency) {
      await dispatch("updateCurrency", userCurrency);
    }
  },

  async fetchEconomicZoneClients({ commit }, currency) {
    commit("SET_ECONOMIC_ZONE_CLIENTS_LOADING", true);
    try {
      const response = await API.get(
        `/economic-zone/${currency.toLowerCase()}/clients?=`
      );

      const clients = response.data;

      // 1️⃣ On stocke la liste complète en Vuex
      commit("SET_ECONOMIC_ZONE_CLIENTS", clients);

      // 2️⃣ On construit un map { [id]: defaultLocaleCode }
      const defaultLocaleMap = clients.reduce((map, client) => {
        map[client.id] =
          client.defaultLocaleCode ||
          (Array.isArray(client.locales) ? client.locales[0] : "fr_FR");
        return map;
      }, {});

      // 3️⃣ On persiste dans le sessionStorage
      sessionStorage.setItem(
        "clientsDefaultLocale",
        JSON.stringify(defaultLocaleMap)
      );
    } catch (error) {
      commit("SET_ECONOMIC_ZONE_CLIENTS_ERROR", error.message);
      commit("SET_ECONOMIC_ZONE_CLIENTS", []);
    } finally {
      commit("SET_ECONOMIC_ZONE_CLIENTS_LOADING", false);
    }
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
